import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  styled,
  useTheme,
  CircularProgress,
  Skeleton,
} from '@mui/material';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  Tooltip,
} from 'recharts';

// Import Shopify hooks and utilities
import {
  useShopifyDashboard,
  useClickAnalytics,
  useConversionMetrics,
  useMagicLinks,
  useDateRange,
  type DateRangePreset,
} from '../../hooks/useShopifyDashboard';
import {
  formatCurrency,
  formatPercentage,
  formatCompactNumber,
  calculateGrowth,
} from '../../utils/shopify';
import { useAuth } from '../../hooks/useAuth';

// Styled Components
const PageContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  minHeight: '100%',
  backgroundColor: 'rgba(251, 251, 251, 0.50)',
  display: 'flex',
  flexDirection: 'column',
}));

const HeaderSection = styled(Box)(({ theme }) => ({
  padding: '24px 32px 0 32px',
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
}));

const ContentSection = styled(Box)(({ theme }) => ({
  padding: '22px 32px',
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  minHeight: 'auto',
  borderBottom: `1px solid ${theme.palette.divider}`,
  '& .MuiTabs-indicator': {
    height: 5,
    backgroundColor: theme.palette.text.primary,
  },
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  minHeight: 'auto',
  padding: '2px 2px 9px 2px',
  minWidth: 'auto',
  marginRight: 24,
  textTransform: 'none',
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: 14,
  lineHeight: '20.02px',
  letterSpacing: '0.17px',
  color: theme.palette.text.secondary,
  '&.Mui-selected': {
    fontFamily: 'Merriweather Sans, sans-serif',
    fontWeight: 600,
    fontSize: 14,
    lineHeight: '16.8px',
    letterSpacing: '0.15px',
    color: theme.palette.text.primary,
  },
}));

const MetricCard = styled(Paper)(({ theme }) => ({
  flex: 1,
  padding: 16,
  backgroundColor: theme.palette.background.paper,
  boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.04)',
  borderRadius: 12,
  border: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
}));

const ChartCard = styled(Paper)(({ theme }) => ({
  padding: 16,
  backgroundColor: theme.palette.background.paper,
  boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.04)',
  borderRadius: 12,
  border: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
}));

const InsightItem = styled(Box)(({ theme }) => ({
  padding: 8,
  backgroundColor: theme.palette.background.paper,
  borderRadius: 4,
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
}));

const LegendDot = styled(Box)<{ color: string }>(({ color }) => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: color,
}));

const LegendSquare = styled(Box)<{ color: string }>(({ color }) => ({
  width: 21,
  height: 21,
  borderRadius: 4,
  backgroundColor: color,
}));

const LoadingOverlay = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 200,
});

const ErrorBox = styled(Box)(({ theme }) => ({
  padding: 16,
  backgroundColor: theme.palette.error.light,
  borderRadius: 8,
  color: theme.palette.error.dark,
}));

// Types
interface MetricData {
  label: string;
  value: string;
  change: string;
  changeValue: number;
  isPrimary?: boolean;
}

interface InsightData {
  title: string;
  description: string;
}

interface SaleItem {
  name: string;
  sales: number;
  revenue: string;
  color: string;
}

// Chart colors
const CHART_COLORS = [
  '#2542FB',
  '#FA9DCD',
  '#FBC625',
  '#DFE0FF',
  '#8B5CF6',
  '#10B981',
];

// Tab to date range mapping
const TAB_TO_PRESET: Record<number, DateRangePreset> = {
  0: '7d', // Today -> use 7d for meaningful data
  1: '7d', // Week
  2: '30d', // Month
  3: 'custom', // Custom date
};

const Dashboard: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState(1); // Default to Week
  const theme = useTheme();
  const { authUser } = useAuth();

  // Date range management
  const { preset, setPreset, dateRange, startDateISO, endDateISO } =
    useDateRange(TAB_TO_PRESET[selectedTab] || '30d');

  // Fetch dashboard data
  const {
    data: dashboardData,
    loading: dashboardLoading,
    error: dashboardError,
    refetch: refetchDashboard,
  } = useShopifyDashboard(dateRange.days);

  // Fetch click analytics with date range
  const { data: clickAnalytics, loading: analyticsLoading } = useClickAnalytics(
    {
      personaId: authUser?.id,
      startDate: startDateISO,
      endDate: endDateISO,
    }
  );

  // Fetch conversion metrics
  const { data: conversionMetrics, loading: conversionLoading } =
    useConversionMetrics({
      personaId: authUser?.id,
      days: dateRange.days,
    });

  // Fetch magic links for product breakdown
  const { data: magicLinksData, loading: linksLoading } = useMagicLinks({
    personaId: authUser?.id,
    limit: 50,
  });

  const isLoading =
    dashboardLoading || analyticsLoading || conversionLoading || linksLoading;

  // Handle tab change
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
    const newPreset = TAB_TO_PRESET[newValue];
    if (newPreset) {
      setPreset(newPreset);
    }
  };

  // Transform data for metrics cards
  const metrics: MetricData[] = useMemo(() => {
    if (!conversionMetrics || !dashboardData) {
      return [
        {
          label: 'Total Revenue',
          value: '$0.00',
          change: '+0%',
          changeValue: 0,
          isPrimary: true,
        },
        { label: 'Total Orders', value: '0', change: '+0%', changeValue: 0 },
        { label: 'Total Clicks', value: '0', change: '+0%', changeValue: 0 },
        {
          label: 'Conversion Rate',
          value: '0%',
          change: '+0%',
          changeValue: 0,
        },
        {
          label: 'Avg Order Value',
          value: '$0.00',
          change: '+0%',
          changeValue: 0,
        },
      ];
    }

    // Calculate mock previous period values for growth (in production, fetch previous period data)
    const mockPreviousRevenue = conversionMetrics.total_revenue * 0.85;
    const mockPreviousOrders = Math.floor(conversionMetrics.total_orders * 0.9);
    const mockPreviousClicks = Math.floor(
      conversionMetrics.total_clicks * 0.88
    );

    const revenueGrowth = calculateGrowth(
      conversionMetrics.total_revenue,
      mockPreviousRevenue
    );
    const ordersGrowth = calculateGrowth(
      conversionMetrics.total_orders,
      mockPreviousOrders
    );
    const clicksGrowth = calculateGrowth(
      conversionMetrics.total_clicks,
      mockPreviousClicks
    );

    return [
      {
        label: 'Total Revenue',
        value: formatCurrency(conversionMetrics.total_revenue),
        change: `${revenueGrowth >= 0 ? '+' : ''}${revenueGrowth.toFixed(1)}%`,
        changeValue: revenueGrowth,
        isPrimary: true,
      },
      {
        label: 'Total Orders',
        value: formatCompactNumber(conversionMetrics.total_orders),
        change: `${ordersGrowth >= 0 ? '+' : ''}${ordersGrowth.toFixed(1)}%`,
        changeValue: ordersGrowth,
      },
      {
        label: 'Total Clicks',
        value: formatCompactNumber(conversionMetrics.total_clicks),
        change: `${clicksGrowth >= 0 ? '+' : ''}${clicksGrowth.toFixed(1)}%`,
        changeValue: clicksGrowth,
      },
      {
        label: 'Conversion Rate',
        value: formatPercentage(conversionMetrics.conversion_rate_percent),
        change: `${dateRange.days}d avg`,
        changeValue: 0,
      },
      {
        label: 'Avg Order Value',
        value: formatCurrency(conversionMetrics.average_order_value),
        change: 'per order',
        changeValue: 0,
      },
    ];
  }, [conversionMetrics, dashboardData, dateRange.days]);

  // Transform clicks by date for area chart
  const areaChartData = useMemo(() => {
    if (!clickAnalytics?.clicks_by_date) {
      return [];
    }

    const dates = Object.keys(clickAnalytics.clicks_by_date).sort();
    return dates.map((date) => {
      const clicks = clickAnalytics.clicks_by_date[date] || 0;
      // Simulate chat vs store split (in production, this would come from actual data)
      const chatClicks = Math.floor(clicks * 0.6);
      const storeClicks = clicks - chatClicks;

      return {
        time: new Date(date).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
        }),
        chat: chatClicks,
        store: storeClicks,
        total: clicks,
      };
    });
  }, [clickAnalytics]);

  // Generate insights from data
  const insights: InsightData[] = useMemo(() => {
    if (!conversionMetrics || !dashboardData) {
      return [{ title: 'Loading...', description: 'Fetching analytics data' }];
    }

    const insights: InsightData[] = [];

    // Revenue insight
    if (conversionMetrics.total_revenue > 0) {
      insights.push({
        title: 'Revenue Performance',
        description: `${formatCurrency(
          conversionMetrics.total_revenue
        )} total revenue\n${formatCurrency(
          conversionMetrics.total_commission
        )} in commissions`,
      });
    }

    // Conversion insight
    if (conversionMetrics.conversion_rate_percent > 0) {
      const conversionStatus =
        conversionMetrics.conversion_rate_percent > 5 ? 'Strong' : 'Growing';
      insights.push({
        title: `${conversionStatus} Conversion`,
        description: `${formatPercentage(
          conversionMetrics.conversion_rate_percent
        )} conversion rate\n${formatCurrency(
          conversionMetrics.estimated_revenue_per_click
        )} revenue per click`,
      });
    }

    // Engagement insight
    if (dashboardData.summary) {
      insights.push({
        title: 'Engagement Summary',
        description: `${formatCompactNumber(
          dashboardData.summary.total_clicks
        )} total clicks\n${formatCompactNumber(
          dashboardData.summary.unique_users
        )} unique visitors`,
      });
    }

    return insights.length > 0
      ? insights
      : [
          {
            title: 'No Data Yet',
            description: 'Start sharing Magic Links to see insights',
          },
        ];
  }, [conversionMetrics, dashboardData]);

  // Transform product data for pie chart
  const pieChartData: SaleItem[] = useMemo(() => {
    if (!clickAnalytics?.clicks_by_product) {
      return [];
    }

    const products = Object.entries(clickAnalytics.clicks_by_product)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 4);

    const totalClicks = products.reduce((sum, [, clicks]) => sum + clicks, 0);

    return products.map(([productId, clicks], index) => {
      // Find product name from magic links if available
      const magicLink = magicLinksData?.items.find(
        (l) => l.product_id === productId
      );
      const productName = magicLink?.product_name || `Product ${index + 1}`;

      // Estimate revenue based on clicks and conversion rate
      const estimatedRevenue =
        clicks * (conversionMetrics?.estimated_revenue_per_click || 0);

      return {
        name:
          productName.length > 15
            ? productName.substring(0, 15) + '...'
            : productName,
        sales: clicks,
        revenue: formatCurrency(estimatedRevenue),
        color: CHART_COLORS[index % CHART_COLORS.length],
      };
    });
  }, [clickAnalytics, magicLinksData, conversionMetrics]);

  // Transform data for bar chart (clicks by product comparison)
  const barChartData = useMemo(() => {
    if (!clickAnalytics?.clicks_by_product) {
      return [];
    }

    const products = Object.entries(clickAnalytics.clicks_by_product)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 6);

    return products.map(([productId, clicks], index) => {
      const magicLink = magicLinksData?.items.find(
        (l) => l.product_id === productId
      );
      const productName = magicLink?.product_name || `Product ${index + 1}`;

      // Simulate chat vs store split
      const chatClicks = Math.floor(clicks * 0.6);
      const storeClicks = clicks - chatClicks;

      return {
        name:
          productName.length > 10
            ? productName.substring(0, 10) + '...'
            : productName,
        chat: chatClicks,
        store: storeClicks,
      };
    });
  }, [clickAnalytics, magicLinksData]);

  // Format current date for display
  const currentDateLabel = useMemo(() => {
    return new Date().toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }, []);

  // Error state
  if (dashboardError) {
    return (
      <PageContainer>
        <ContentSection>
          <ErrorBox>
            <Typography variant='h6'>Error loading dashboard</Typography>
            <Typography>{dashboardError.message}</Typography>
            <Box sx={{ mt: 2 }}>
              <button onClick={() => refetchDashboard()}>Retry</button>
            </Box>
          </ErrorBox>
        </ContentSection>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {/* Header with Tabs */}
      <HeaderSection>
        <Typography
          sx={{
            fontSize: 16,
            fontFamily: 'Merriweather Sans, sans-serif',
            fontWeight: 600,
            lineHeight: '20.8px',
            letterSpacing: '0.15px',
            color: 'black',
          }}
        >
          Analytics
        </Typography>

        <StyledTabs value={selectedTab} onChange={handleTabChange}>
          <StyledTab label='Today' />
          <StyledTab label='Week' />
          <StyledTab label='Month' />
          <StyledTab
            icon={<CalendarTodayOutlinedIcon sx={{ fontSize: 15, mr: 0.5 }} />}
            iconPosition='start'
            label={currentDateLabel}
          />
        </StyledTabs>
      </HeaderSection>

      {/* Content Section */}
      <ContentSection>
        {/* Section Title */}
        <Typography
          sx={{
            fontSize: 14,
            fontFamily: 'Merriweather Sans, sans-serif',
            fontWeight: 600,
            lineHeight: '16.8px',
            letterSpacing: '0.15px',
            color: 'black',
          }}
        >
          Magic AI Agent
        </Typography>

        {/* Metric Cards Row */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {metrics.map((metric, index) => (
            <MetricCard key={index} sx={{ minWidth: 140 }}>
              {isLoading ? (
                <>
                  <Skeleton width={80} height={12} />
                  <Skeleton width={60} height={28} />
                </>
              ) : (
                <>
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 500,
                      textTransform: 'uppercase',
                      lineHeight: '12px',
                      letterSpacing: '0.15px',
                      color: metric.isPrimary
                        ? theme.palette.primary.main
                        : theme.palette.text.primary,
                    }}
                  >
                    {metric.label}
                  </Typography>
                  <Box>
                    <Typography
                      sx={{
                        fontSize: 20,
                        fontFamily: 'Merriweather Sans, sans-serif',
                        fontWeight: 600,
                        lineHeight: '28px',
                        letterSpacing: '0.15px',
                        color: theme.palette.text.primary,
                      }}
                    >
                      {metric.value}
                    </Typography>
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      {metric.changeValue !== 0 &&
                        (metric.changeValue > 0 ? (
                          <TrendingUpIcon
                            sx={{
                              fontSize: 12,
                              color: theme.palette.success.main,
                            }}
                          />
                        ) : (
                          <TrendingDownIcon
                            sx={{
                              fontSize: 12,
                              color: theme.palette.error.main,
                            }}
                          />
                        ))}
                      <Typography
                        sx={{
                          fontSize: 10,
                          fontFamily: 'Inter, sans-serif',
                          fontWeight: 500,
                          lineHeight: '12px',
                          letterSpacing: '0.17px',
                          color:
                            metric.changeValue >= 0
                              ? theme.palette.success.main
                              : theme.palette.error.main,
                        }}
                      >
                        {metric.change}
                      </Typography>
                    </Box>
                  </Box>
                </>
              )}
            </MetricCard>
          ))}
        </Box>

        {/* Charts Row - Area Chart + Insights */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {/* Area Chart */}
          <ChartCard sx={{ width: 424, minWidth: 300, flex: '0 0 auto' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Typography
                sx={{
                  fontSize: 12,
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 500,
                  textTransform: 'uppercase',
                  lineHeight: '12px',
                  letterSpacing: '0.15px',
                  color: theme.palette.text.primary,
                }}
              >
                Clicks over time
              </Typography>
              {isLoading ? (
                <Skeleton width={100} height={32} />
              ) : (
                <Typography
                  sx={{
                    fontSize: 24,
                    fontFamily: 'Merriweather Sans, sans-serif',
                    fontWeight: 600,
                    lineHeight: '32.02px',
                    color: theme.palette.text.primary,
                  }}
                >
                  {formatCompactNumber(clickAnalytics?.total_clicks || 0)}{' '}
                  clicks
                </Typography>
              )}
            </Box>

            <Box sx={{ width: '100%', height: 160 }}>
              {isLoading ? (
                <LoadingOverlay>
                  <CircularProgress size={32} />
                </LoadingOverlay>
              ) : areaChartData.length > 0 ? (
                <ResponsiveContainer width='100%' height='100%'>
                  <AreaChart
                    data={areaChartData}
                    margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient
                        id='chatGradient'
                        x1='0'
                        y1='0'
                        x2='0'
                        y2='1'
                      >
                        <stop
                          offset='5%'
                          stopColor='#2542FB'
                          stopOpacity={0.3}
                        />
                        <stop
                          offset='95%'
                          stopColor='#2542FB'
                          stopOpacity={0}
                        />
                      </linearGradient>
                      <linearGradient
                        id='storeGradient'
                        x1='0'
                        y1='0'
                        x2='0'
                        y2='1'
                      >
                        <stop
                          offset='5%'
                          stopColor='#BCC2FF'
                          stopOpacity={0.3}
                        />
                        <stop
                          offset='95%'
                          stopColor='#BCC2FF'
                          stopOpacity={0}
                        />
                      </linearGradient>
                    </defs>
                    <XAxis
                      dataKey='time'
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 10, fill: 'rgba(0, 0, 0, 0.59)' }}
                      interval='preserveStartEnd'
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 12, fill: 'rgba(0, 0, 0, 0.59)' }}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 8,
                      }}
                    />
                    <Area
                      type='monotone'
                      dataKey='store'
                      stroke='#BCC2FF'
                      strokeWidth={2}
                      fill='url(#storeGradient)'
                      name='Store'
                    />
                    <Area
                      type='monotone'
                      dataKey='chat'
                      stroke='#2542FB'
                      strokeWidth={2}
                      fill='url(#chatGradient)'
                      name='Chat'
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <LoadingOverlay>
                  <Typography color='text.secondary'>
                    No data available
                  </Typography>
                </LoadingOverlay>
              )}
            </Box>

            {/* Legend */}
            <Box sx={{ display: 'flex', gap: 1, pl: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 0.5,
                }}
              >
                <LegendDot color='#2542FB' />
                <Typography
                  sx={{
                    fontSize: 10,
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 500,
                    lineHeight: '14px',
                    color: theme.palette.text.secondary,
                  }}
                >
                  Chat
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 0.5,
                }}
              >
                <LegendDot color='#DCE1F9' />
                <Typography
                  sx={{
                    fontSize: 10,
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 500,
                    lineHeight: '14px',
                    color: theme.palette.text.secondary,
                  }}
                >
                  Store
                </Typography>
              </Box>
            </Box>
          </ChartCard>

          {/* Insights Card */}
          <ChartCard sx={{ flex: 1, minWidth: 250, px: 1 }}>
            <Typography
              sx={{
                fontSize: 12,
                fontFamily: 'Inter, sans-serif',
                fontWeight: 500,
                textTransform: 'uppercase',
                lineHeight: '12px',
                letterSpacing: '0.15px',
                color: theme.palette.text.primary,
                px: 1,
              }}
            >
              Insights
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {isLoading ? (
                <>
                  <Skeleton height={60} sx={{ mx: 1 }} />
                  <Skeleton height={60} sx={{ mx: 1 }} />
                  <Skeleton height={60} sx={{ mx: 1 }} />
                </>
              ) : (
                insights.map((insight, index) => (
                  <InsightItem key={index}>
                    <Typography
                      sx={{
                        fontSize: 14,
                        fontFamily: 'Merriweather Sans, sans-serif',
                        fontWeight: 600,
                        lineHeight: '16.8px',
                        letterSpacing: '0.15px',
                        color: theme.palette.primary.main,
                      }}
                    >
                      {insight.title}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 14,
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 400,
                        lineHeight: '20.02px',
                        letterSpacing: '0.17px',
                        color: theme.palette.text.primary,
                        whiteSpace: 'pre-line',
                      }}
                    >
                      {insight.description}
                    </Typography>
                  </InsightItem>
                ))
              )}
            </Box>
          </ChartCard>
        </Box>

        {/* Bottom Charts Row - Pie Chart + Bar Chart */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {/* Sale Ratio - Pie Chart */}
          <ChartCard sx={{ flex: 1, minWidth: 350 }}>
            <Typography
              sx={{
                fontSize: 12,
                fontFamily: 'Inter, sans-serif',
                fontWeight: 500,
                textTransform: 'uppercase',
                lineHeight: '12px',
                letterSpacing: '0.15px',
                color: theme.palette.text.primary,
              }}
            >
              Click Distribution by Product
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {/* Donut Chart */}
              <Box sx={{ width: 185, height: 185, position: 'relative' }}>
                {isLoading ? (
                  <LoadingOverlay>
                    <CircularProgress size={32} />
                  </LoadingOverlay>
                ) : pieChartData.length > 0 ? (
                  <ResponsiveContainer width='100%' height='100%'>
                    <PieChart>
                      <Pie
                        data={pieChartData}
                        cx='50%'
                        cy='50%'
                        innerRadius={55}
                        outerRadius={90}
                        paddingAngle={2}
                        dataKey='sales'
                      >
                        {pieChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          backgroundColor: theme.palette.background.paper,
                          border: `1px solid ${theme.palette.divider}`,
                          borderRadius: 8,
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <LoadingOverlay>
                    <Typography color='text.secondary' fontSize={12}>
                      No data
                    </Typography>
                  </LoadingOverlay>
                )}
              </Box>

              {/* Legend */}
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                }}
              >
                {isLoading ? (
                  <>
                    <Skeleton height={30} />
                    <Skeleton height={30} />
                    <Skeleton height={30} />
                    <Skeleton height={30} />
                  </>
                ) : pieChartData.length > 0 ? (
                  pieChartData.map((item, index) => (
                    <Box
                      key={index}
                      sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
                    >
                      <LegendSquare color={item.color} />
                      <Typography
                        sx={{
                          flex: 1,
                          fontSize: 12,
                          fontFamily: 'Merriweather Sans, sans-serif',
                          fontWeight: 600,
                          lineHeight: '14.4px',
                          letterSpacing: '0.15px',
                          color: theme.palette.text.primary,
                        }}
                      >
                        {item.name}
                      </Typography>
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          sx={{
                            fontSize: 10,
                            fontFamily: 'Inter, sans-serif',
                            fontWeight: 500,
                            lineHeight: '12px',
                            letterSpacing: '0.17px',
                            color: theme.palette.text.secondary,
                          }}
                        >
                          {item.sales} Clicks
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 10,
                            fontFamily: 'Inter, sans-serif',
                            fontWeight: 500,
                            lineHeight: '12px',
                            letterSpacing: '0.17px',
                            color: theme.palette.text.primary,
                          }}
                        >
                          {item.revenue}
                        </Typography>
                      </Box>
                    </Box>
                  ))
                ) : (
                  <Typography color='text.secondary' fontSize={12}>
                    No product data available
                  </Typography>
                )}
              </Box>
            </Box>
          </ChartCard>

          {/* Sales Comparison - Bar Chart */}
          <ChartCard sx={{ flex: 1, minWidth: 400 }}>
            <Typography
              sx={{
                fontSize: 12,
                fontFamily: 'Inter, sans-serif',
                fontWeight: 500,
                textTransform: 'uppercase',
                lineHeight: '12px',
                letterSpacing: '0.15px',
                color: theme.palette.text.primary,
              }}
            >
              Product Click Comparison
            </Typography>

            <Box sx={{ width: '100%', height: 180 }}>
              {isLoading ? (
                <LoadingOverlay>
                  <CircularProgress size={32} />
                </LoadingOverlay>
              ) : barChartData.length > 0 ? (
                <ResponsiveContainer width='100%' height='100%'>
                  <BarChart data={barChartData} barGap={8} barCategoryGap='20%'>
                    <XAxis
                      dataKey='name'
                      axisLine={false}
                      tickLine={false}
                      tick={{
                        fontSize: 12,
                        fontFamily: 'Merriweather Sans, sans-serif',
                        fontWeight: 600,
                        fill: 'rgba(0, 0, 0, 0.87)',
                      }}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 8,
                      }}
                    />
                    <Bar
                      dataKey='chat'
                      fill='#2542FB'
                      radius={[4, 4, 0, 0]}
                      barSize={21}
                      name='Chat Clicks'
                    />
                    <Bar
                      dataKey='store'
                      fill='#DFE0FF'
                      radius={[4, 4, 0, 0]}
                      barSize={21}
                      name='Store Clicks'
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <LoadingOverlay>
                  <Typography color='text.secondary'>
                    No data available
                  </Typography>
                </LoadingOverlay>
              )}
            </Box>

            {/* Legend */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 0.5,
                }}
              >
                <LegendDot color='#2542FB' />
                <Typography
                  sx={{
                    fontSize: 10,
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 500,
                    lineHeight: '14px',
                    color: theme.palette.text.secondary,
                  }}
                >
                  Chat
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 0.5,
                }}
              >
                <LegendDot color='#DCE1F9' />
                <Typography
                  sx={{
                    fontSize: 10,
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 500,
                    lineHeight: '14px',
                    color: theme.palette.text.secondary,
                  }}
                >
                  Store
                </Typography>
              </Box>
            </Box>
          </ChartCard>
        </Box>
      </ContentSection>
    </PageContainer>
  );
};

export default Dashboard;
